
import PageLayout, { siteTitle } from "@/components/PageLayout";
import SearchBuilder from "@/components/search_builder.js";
import DropdownFilter from "@/components/dropdown_filter";
import ApplicationPhotoTile from '@/components/inspiration/application_photos/application_photo_tile.js';
import PhotoFilterPillBar from "@/components/inspiration/photo_filter_pill_bar";
import { useState, useEffect, useContext } from "react";
import Qs from "qs";
import ApplicationPhotoService from '@/lib/services/application_photos';
import searchTranslator from "@/lib/search_translator.js";
import { loadConfig } from "@/lib/utils.js";
import Link from "next/link";
import SearchToggle from "@/components/inspiration/search_toggle";
import Lightbox from "@/components/lightbox";
import { is_mobile } from "@/lib/utils.js";
import InspirationBreadcrumbs from '@/components/inspiration/breadcrumbs';
import {
  ApplicationPhotoCacheContext
} from '@/lib/inspiration_cache_context.js';


export default function ApplicationPhotoSearchPage(results) {
  const {
    application_photos,
    total_count,
    initialSearch,
    page_layout,
    favicon,
    stylesheet,
    cart_enabled,
    application_category_filters,
    brand_filters,
    space_filters,
    product_type_filters,
    next_page,
    contained_product_name,
    inspiration_display,
    mobile, 
    scroll
  } = results;

  const applicationPhotoCache = useContext(
    ApplicationPhotoCacheContext
  );

  const [isLoading, setIsLoading] = useState(false);
  const [lightBoxIndex, setLightBoxIndex] = useState(null);

  const {
    items: photos,
    setItems,
    search,
    setSearch,
    totalCount,
    setTotalCount,
    applicationCategoryFilters,
    setApplicationCategoryFilters,
    spaceFilters,
    setSpaceFilters,
    productTypeFilters,
    setProductTypeFilters,
    brandFilters,
    setBrandFilters,
    nextPage,
    setNextPage
  } = applicationPhotoCache;

  const sort = false;

  /*
   * Initialize the cache from the SSR response.
   *
   * This only happens when the cache doesn't already contain photos.
   * That allows the cached collection to survive navigating to an
   * individual application photo and coming back.
   */
  useEffect(() => {
    if (!applicationPhotoCache.items?.length) {
      setItems(application_photos || []);
      setTotalCount(total_count || 0);
      setNextPage(next_page);
      setSearch(initialSearch || {});

      setApplicationCategoryFilters(
        application_category_filters || []
      );

      setSpaceFilters(
        space_filters || []
      );

      setProductTypeFilters(
        product_type_filters || []
      );

      setBrandFilters(
        brand_filters || []
      );

    }
    if (scroll) {
      window.scrollTo({top: scroll, behavior: 'instant'} )
    }

  }, []);

  const showLightBox = (e, index) => {
    e.stopPropagation();
    setLightBoxIndex(index);
  };

  const hideLightBox = () => {
    setLightBoxIndex(null);
    document.body.style.overflow = "visible";
  };

  const photoFilters = [
    [
      'application_categories_id_eq',
      'Application Types',
      applicationCategoryFilters
    ],
    [
      'application_spaces_id_eq',
      'Spaces',
      spaceFilters
    ],
    [
      'tags_id_eq',
      'Product Types',
      productTypeFilters
    ]
  ];

  if (brandFilters) {
    photoFilters.push([
      'company_id_eq',
      'Brand Filter',
      brandFilters
    ]);
  }

  const breadcrumbs = [
    {
      url: '/inspiration/application_photos/index',
      name: 'Application Photos'
    }
  ];

  const searchAppPhotos = (newSearch, pageChange) => {
    setIsLoading(true);

    fetch(
      "/lex-api/inspiration/application_photos/all" +
      "?" +
      Qs.stringify(newSearch, {
        arrayFormat: "brackets",
        encode: true
      })
    )
      .then((res) => res.json())
      .then((data) => {
        setIsLoading(false);

        setItems(data.application_photos || []);
        setTotalCount(data.total_count || 0);

        setApplicationCategoryFilters(
          data.application_category_filters || []
        );

        setSpaceFilters(
          data.space_filters || []
        );

        setProductTypeFilters(
          data.product_type_filters || []
        );

        if (data.brand_filters) {
          setBrandFilters(data.brand_filters);
        }

        setNextPage(data.next_page);
        setSearch(newSearch || {});
      });
  };

  const loadMoreAppPhotos = (currentSearch) => {
    setIsLoading(true);

    const newSearch = {
      ...(currentSearch || {}),
      page: nextPage
    };

    fetch(
      "/lex-api/inspiration/application_photos/all" +
      "?" +
      Qs.stringify(newSearch, {
        arrayFormat: "brackets",
        encode: true
      })
    )
      .then((res) => res.json())
      .then((data) => {
        setIsLoading(false);

        setItems((currentPhotos) => [
          ...currentPhotos,
          ...(data.application_photos || [])
        ]);

        setNextPage(data.next_page);
      });
  };

  return (
    <PageLayout
      title="Inspiration Search"
      page_layout={page_layout}
      cart_enabled={cart_enabled}
      favicon={favicon}
      stylesheet={stylesheet}
      inspirationBreadcrumbs={
        <InspirationBreadcrumbs breadcrumbs_path={breadcrumbs} />
      }
    >
      <SearchBuilder
        initialSearch={search}
        search={searchAppPhotos}
        useNextRouterForSearchUrl={true}

      >
        {(searchState) => {
          return (
            <div className='lexcms-inspiration'>

              <div className='lexcms-inspiration-search-container'>

                <div className="lexcms-inspiration-search-header-text">
                  <h1>Inspirational Search</h1>
                  <p>
                    Discover awe-inspiring images from our projects.
                  </p>
                </div>

                <div className='lexcms-inspiration-sidebar-filters'>

                  {inspiration_display ==
                    'both_application_photos_and_galleries' &&
                    <SearchToggle
                      display={inspiration_display}
                      photosSelected={true}
                    />
                  }

                  <p className='lexcms-inspiration-search-refine-photos-count'>
                    Refine results:
                  </p>

                  {photoFilters.map((d, i) => {
                    return (
                      <DropdownFilter
                        key={i}
                        searchKey={`q[${d[0]}]`}
                        className={"inspiration-search-sidebar-filter"}
                        searchState={searchState}
                        options={d[2]}
                        label={d[1]}
                        disableFirst={true}
                      />
                    );
                  })}

                  <Link href='/lex-cms/inspiration/highlights'>
                    <a
                      className='lexcms-regular-button lexcms-clear-photos-button'
                      onClick={() => {
                        searchAppPhotos({});
                        searchState.clearAll();
                      }}
                    >
                      Reset All Selections
                    </a>
                  </Link>

                </div>
              </div>

              <div className='lexcms-search-results app-type-photos-container'>

                <div className='lexcms-filters-container lexcms-results-top'>

                  <PhotoFilterPillBar
                    searchState={searchState}
                    application_categories={applicationCategoryFilters}
                    space_filters={spaceFilters}
                    product_type_filters={productTypeFilters}
                    resourceName={'photos'}
                    filters={photoFilters}
                    total_count={totalCount}
                    searchAppPhotos={searchAppPhotos}
                    contained_product_name={contained_product_name}
                  />

                  {sort &&
                    <div className='lexcms-inspiration-search-sort-container'>
                      <DropdownFilter
                        searchKey={"q[s]"}
                        className={"inspiration-search-sort-filter"}
                        searchState={searchState}
                        options={[
                          {
                            value: 'has_hotspot DESC',
                            label: 'Has Hotspot'
                          }
                        ]}
                        label="Sort"
                        disableFirst={true}
                      />
                    </div>
                  }

                </div>

                <div className='lexcms-result-container'>

                  <div className='app-photo-list'>
                    {photos?.map((d, i) => {
                      return (
                        <ApplicationPhotoTile
                          app_photo={d}
                          key={d.id}
                          index={i}
                          showLightBox={showLightBox}
                          mobile={mobile}
                        />
                      );
                    })}
                  </div>

                </div>

                {nextPage &&
                  <div className='photos-remaining-link'>
                    <a
                      style={{ cursor: 'pointer' }}
                      onClick={() => {
                        loadMoreAppPhotos(searchState.search);
                      }}
                    >
                      Load More Images
                    </a>
                  </div>
                }

              </div>

              <Lightbox
                index={lightBoxIndex}
                images={photos}
                hideLightBox={hideLightBox}
              />

            </div>
          );
        }}
      </SearchBuilder>
    </PageLayout>
  );
}


export async function getServerSideProps(context) {
  let token = context?.req?.headers['lex-api-token'];

  let service = await ApplicationPhotoService.build(
    token,
    context.req?.headers.host
  );

  let q = searchTranslator(context.query);

  let search = {
    initialSearch: q
  };

  let mobile = is_mobile(
    context.req?.headers['user-agent']
  );

  let pageConfig = loadConfig(service);



  if (context.query?.clientNavigation) {

    return {
      props: {
        ...pageConfig,
        ...search,
        mobile, 
        scroll: context.query?.scroll || null
      }
    };
  }


  let apiFriendlyQ = searchTranslator(
    context.query,
    true
  );

  let results = await service.index(apiFriendlyQ);

  return {
    props: {
      ...results,
      ...pageConfig,
      ...search,
      mobile
    }
  };
}

